import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { createContext, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import shouldShowTutorial from "../utils/shouldShowTutorial";

const AuthContext = createContext();

export const AuthContextProvider = ({ children }) => {
  const queryClient = useQueryClient();
  const [accessToken, setAccessToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const storedAccessToken = localStorage.getItem("access");
  const [user, setUser] = useState(() => {
    return storedAccessToken ? jwtDecode(storedAccessToken) : null;
  });
  // False when a stored token exists and needs async verification; true once known
  const [authChecked, setAuthChecked] = useState(!storedAccessToken);
  const [shouldShowTutorialBool, setShouldShowTutorialBool] = useState(false);
  const [isZeroPhaseTutorialOpen, setIsZeroPhaseTutorialOpen] = useState(false);

  const navigate = useNavigate();
  const env = process.env.REACT_APP_ENV || "development";

  const baseURLMapping = {
    production: process.env.REACT_APP_BASE_URL_PRODUCTION,
    staging: process.env.REACT_APP_BASE_URL_STAGING,
    development: process.env.REACT_APP_BASE_URL_DEV, // Default to dev
  };

  const baseURL = `${
    baseURLMapping[env] || process.env.REACT_APP_BASE_URL_DEV
  }/auth`;

  const authApi = axios.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
  });

  const login = async (email, password) => {
    try {
      const response = await authApi.post("/token/create/", {
        email,
        password,
      });

      if (response.status === 200) {
        const { access, refresh } = response.data;

        queryClient.clear();
        setAccessToken(access);
        setRefreshToken(refresh);
        setUser(jwtDecode(access));

        localStorage.setItem("access", access);
        localStorage.setItem("refresh", refresh);
        localStorage.setItem(
          "should_never_show_tutorial",
          jwtDecode(access).should_never_show_tutorial
        );

        return response;
      }
    } catch (error) {
      console.error("Login failed:", error);
      throw error;
    }
  };

  const requestPasswordReset = async (email) => {
    try {
      const response = await authApi.post("/password/reset/request/", {
        email,
      });
      return response;
    } catch (error) {
      console.error("Password reset request failed:", error);
      throw error;
    }
  };

  const confirmPasswordReset = async ({ uid, token, new_password }) => {
    try {
      const response = await authApi.post("/password/reset/confirm/", {
        uid,
        token,
        new_password,
      });
      return response;
    } catch (error) {
      console.error("Password reset confirmation failed:", error);
      throw error;
    }
  };

  const logout = () => {
    queryClient.clear();
    setAccessToken(null);
    setRefreshToken(null);
    setUser(null);
    localStorage.removeItem("access");
    localStorage.removeItem("refresh");
    localStorage.removeItem("should_never_show_tutorial");
    navigate("/login");
  };

  const verifyToken = async (token) => {
    try {
      const response = await authApi.post("/token/verify/", { token });
      if (response.status === 200) {
        return true;
      }
    } catch (error) {
      console.error(
        "Valida\u00e7\u00e3o do Access token falhou:",
        error.response?.data || error.message
      );
      return false;
    }
  };

  const handleRefreshToken = async (token) => {
    try {
      const response = await authApi.post("/token/refresh/", {
        refresh: token,
      });
      if (response.status === 200) {
        const { access, refresh: newRefresh } = response.data;
        setAccessToken(access);
        localStorage.setItem("access", access);
        if (newRefresh) {
          setRefreshToken(newRefresh);
          localStorage.setItem("refresh", newRefresh);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error(
        "Erro ao atualizar token:",
        error.response?.data || error.message
      );
      return false;
    }
  };

  const handleTokenVerification = async () => {
    const storedAccessToken = localStorage.getItem("access");
    const storedRefreshToken = localStorage.getItem("refresh");

    if (!storedAccessToken || !storedRefreshToken) {
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      queryClient.clear();
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      setAuthChecked(true);
      navigate("/login");
      return;
    }

    try {
      const isVerified = await verifyToken(storedAccessToken);

      if (isVerified) {
        setAccessToken(storedAccessToken);
        setRefreshToken(storedRefreshToken);
        setUser(jwtDecode(storedAccessToken));
        setAuthChecked(true);
        return;
      }

      const isRefreshed = await handleRefreshToken(storedRefreshToken);

      if (isRefreshed) {
        const newAccess = localStorage.getItem("access");
        setUser(jwtDecode(newAccess));
        setAuthChecked(true);
        return;
      }

      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      queryClient.clear();
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      setAuthChecked(true);
      navigate("/login");
    } catch (error) {
      console.error("Token verification failed:", error);
      localStorage.removeItem("access");
      localStorage.removeItem("refresh");
      queryClient.clear();
      setAccessToken(null);
      setRefreshToken(null);
      setUser(null);
      setAuthChecked(true);
      navigate("/login");
    }
  };

  useEffect(() => {
    const currentAccessToken = accessToken || localStorage.getItem("access");
    const currentRefreshToken = refreshToken || localStorage.getItem("refresh");

    if (!user || !currentAccessToken || !currentRefreshToken) {
      return undefined;
    }

    let timeoutId;
    let cancelled = false;

    try {
      const decodedToken = jwtDecode(currentAccessToken);
      const expirationMs = (decodedToken?.exp || 0) * 1000;

      if (!expirationMs) {
        return undefined;
      }

      const refreshDelayMs = Math.max(expirationMs - Date.now() - 60_000, 0);

      timeoutId = setTimeout(async () => {
        if (cancelled) {
          return;
        }

        const refreshed = await handleRefreshToken(currentRefreshToken);

        if (!refreshed) {
          logout();
          return;
        }

        const newAccessToken = localStorage.getItem("access");
        if (newAccessToken) {
          setUser(jwtDecode(newAccessToken));
        }
      }, refreshDelayMs);
    } catch (error) {
      console.error("Failed to schedule token refresh:", error);
    }

    return () => {
      cancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, refreshToken, user]);

  const postShouldNeverShowTutorial = async () => {
    try {
      const response = await authApi.post(
        "/never-show-tutorial/",
        {},
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("access")}`,
          },
        }
      );
      localStorage.setItem("should_never_show_tutorial", true);
      setUser((prevUser) => ({
        ...prevUser,
        should_never_show_tutorial: true,
      }));
      setShouldShowTutorialBool(false);
      return response;
    } catch (error) {
      console.error("Erro ao postar should_never_show_tutorial:", error);
    }
  };

  const refreshUser = async () => {
    const storedAccessToken = localStorage.getItem("access");
    const storedRefreshToken = localStorage.getItem("refresh");

    if (!storedAccessToken || !storedRefreshToken) {
      console.error(
        "Tokens n\u00e3o encontrados ao tentar atualizar usu\u00e1rio"
      );
      return;
    }

    try {
      // Primeiro tenta verificar se o token atual \u00e9 v\u00e1lido
      const isVerified = await verifyToken(storedAccessToken);

      if (isVerified) {
        // Se o token \u00e9 v\u00e1lido, decodifica e atualiza
        const decodedUser = jwtDecode(storedAccessToken);
        console.log("Usu\u00e1rio atualizado:", decodedUser);
        setUser(decodedUser);
        setAccessToken(storedAccessToken);
      } else {
        // Se n\u00e3o \u00e9 v\u00e1lido, tenta fazer refresh
        console.log("Token inv\u00e1lido, fazendo refresh...");
        const isRefreshed = await handleRefreshToken(storedRefreshToken);

        if (isRefreshed) {
          const newAccess = localStorage.getItem("access");
          const decodedUser = jwtDecode(newAccess);
          console.log("Token atualizado, usu\u00e1rio:", decodedUser);
          setUser(decodedUser);
          setAccessToken(newAccess);
        } else {
          console.error("N\u00e3o foi poss\u00edvel atualizar o token");
        }
      }
    } catch (error) {
      console.error("Erro ao atualizar dados do usu\u00e1rio:", error);
    }
  };

  useEffect(() => {
    if (user) {
      const localShouldNeverShow = localStorage.getItem(
        "should_never_show_tutorial"
      );
      const shouldNeverShowTutorial = localShouldNeverShow
        ? JSON.parse(localShouldNeverShow)
        : user.should_never_show_tutorial;

      const shouldShow = shouldShowTutorial({
        shouldNeverShowTutorial: shouldNeverShowTutorial,
        firstLogin: user.first_login,
        lastLogin: user.last_login,
      });

      setShouldShowTutorialBool(shouldShow);
    }
  }, [user]);

  return (
    <AuthContext.Provider
      value={{
        login,
        logout,
        user,
        authChecked,
        accessToken,
        refreshToken,
        verifyToken,
        handleRefreshToken,
        handleTokenVerification,
        shouldShowTutorialBool,
        setShouldShowTutorialBool,
        isZeroPhaseTutorialOpen,
        setIsZeroPhaseTutorialOpen,
        postShouldNeverShowTutorial,
        refreshUser,
        requestPasswordReset,
        confirmPasswordReset,
      }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
